import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';

const apiRoot = () => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
  return apiUrl.replace(/\/api\/?$/, '');
};

const scopeLabels = {
  'projects:read': 'View your Juree projects',
  'import_drafts:create': 'Create translation import drafts',
  'import_drafts:read': 'Check import draft status'
};

const OAuthAuthorize = () => {
  const {
    isGoogleAuthenticated,
    isGoogleLoading,
    googleAccessToken,
    googleLogin,
    googleUserInfo
  } = useAuth();

  const [client, setClient] = useState(null);
  const [loadingClient, setLoadingClient] = useState(true);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState('');

  const params = useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return {
      response_type: searchParams.get('response_type') || '',
      client_id: searchParams.get('client_id') || '',
      redirect_uri: searchParams.get('redirect_uri') || '',
      scope: searchParams.get('scope') || '',
      state: searchParams.get('state') || '',
      code_challenge: searchParams.get('code_challenge') || '',
      code_challenge_method: searchParams.get('code_challenge_method') || 'S256',
      resource: searchParams.get('resource') || ''
    };
  }, []);

  const requestedScopes = useMemo(() => (
    params.scope
      ? params.scope.split(/\s+/).filter(Boolean)
      : ['projects:read', 'import_drafts:create', 'import_drafts:read']
  ), [params.scope]);

  useEffect(() => {
    const loadClient = async () => {
      if (!params.client_id || !params.redirect_uri) {
        setError('Missing OAuth client information.');
        setLoadingClient(false);
        return;
      }

      try {
        const url = new URL(`${apiRoot()}/oauth/client-info`);
        url.searchParams.set('client_id', params.client_id);
        url.searchParams.set('redirect_uri', params.redirect_uri);

        const response = await fetch(url.toString());
        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.error || 'Failed to load OAuth client');
        }

        setClient(data.client);
      } catch (loadError) {
        console.error('Error loading OAuth client:', loadError);
        setError(loadError.message || 'Failed to load OAuth client.');
      } finally {
        setLoadingClient(false);
      }
    };

    loadClient();
  }, [params.client_id, params.redirect_uri]);

  const handleLogin = () => {
    sessionStorage.setItem('post_login_redirect', `${window.location.pathname}${window.location.search}`);
    googleLogin();
  };

  const handleApprove = async () => {
    if (!googleAccessToken) {
      toast.error('Please sign in with Google first.');
      return;
    }

    setApproving(true);
    setError('');

    try {
      const response = await fetch(`${apiRoot()}/oauth/authorize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${googleAccessToken}`
        },
        body: JSON.stringify(params)
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error_description || data.error || 'Unable to authorize Claude.');
      }

      window.location.href = data.redirectTo;
    } catch (approveError) {
      console.error('OAuth approval failed:', approveError);
      setError(approveError.message || 'Unable to authorize Claude.');
      toast.error(approveError.message || 'Unable to authorize Claude.');
    } finally {
      setApproving(false);
    }
  };

  if (loadingClient) {
    return (
      <div className="max-w-xl mx-auto mt-16 bg-white border border-gray-200 rounded-lg p-8 shadow-sm">
        <p className="text-gray-600">Loading connector authorization...</p>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto mt-12 bg-white border border-gray-200 rounded-lg p-8 shadow-sm">
      <div className="mb-6">
        <p className="text-sm font-medium text-blue-700 mb-2">Claude Connector</p>
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">
          Connect {client?.name || 'Claude'} to Juree
        </h1>
        <p className="text-gray-600">
          Authorize Claude to create translation import drafts in your Juree account.
        </p>
      </div>

      {error && (
        <div className="mb-5 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="mb-6 rounded-md border border-gray-200 p-4">
        <p className="text-sm font-medium text-gray-900 mb-3">Requested access</p>
        <ul className="space-y-2">
          {requestedScopes.map((scope) => (
            <li key={scope} className="text-sm text-gray-700 flex items-start gap-2">
              <span className="mt-1 h-2 w-2 rounded-full bg-blue-600 flex-none" />
              <span>{scopeLabels[scope] || scope}</span>
            </li>
          ))}
        </ul>
      </div>

      {isGoogleAuthenticated ? (
        <div className="mb-6 rounded-md bg-gray-50 px-4 py-3 text-sm text-gray-700">
          Signed in as <span className="font-medium">{googleUserInfo?.email || 'your Google account'}</span>
        </div>
      ) : (
        <div className="mb-6 rounded-md bg-yellow-50 border border-yellow-200 px-4 py-3 text-sm text-yellow-800">
          Sign in with Google to continue with your Juree account.
        </div>
      )}

      <div className="flex justify-end gap-3">
        {!isGoogleAuthenticated ? (
          <button
            type="button"
            onClick={handleLogin}
            disabled={isGoogleLoading}
            className="px-4 py-2 rounded-md bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-60"
          >
            {isGoogleLoading ? 'Signing in...' : 'Sign in with Google'}
          </button>
        ) : (
          <button
            type="button"
            onClick={handleApprove}
            disabled={approving}
            className="px-4 py-2 rounded-md bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-60"
          >
            {approving ? 'Connecting...' : 'Allow Claude'}
          </button>
        )}
      </div>
    </div>
  );
};

export default OAuthAuthorize;
